// ProfileTemplate — for the Team / People category.
// Single-person profile (RG is a one-person studio per the corpus).
// Layout: stylized initial-glyph + role eyebrow + name display + bio + optional quote.
// Reference: Stripe Press author pages, Linear "Meet the team" personal cards.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface ProfileProps {
  content: SceneContent;
}

export function ProfileTemplate({ content }: ProfileProps) {
  const { headline, body, eyebrow, citation } = content;
  // Use the first letter of the headline as the stylized initial
  const initial = (headline || '?').trim().charAt(0).toUpperCase();

  return (
    <Stage>
      {/* Orb anchor — bottom-right, 40px (deferent to the person) */}
      <div className="absolute bottom-12 right-12 max-md:bottom-6 max-md:right-6">
        <Orb size={40} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.12)}
        className="m-auto w-full max-w-[1280px] grid grid-cols-[auto_1fr] gap-16 max-md:grid-cols-1 max-md:gap-10 items-center"
      >
        {/* Portrait — stylized initial inside a soft circle */}
        <motion.div
          variants={fadeUp}
          className="relative w-[280px] h-[280px] max-md:w-[180px] max-md:h-[180px] max-md:mx-auto rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-void-700 via-void-800 to-void-900 border border-fg-faint/40"
        >
          <span className="font-display text-[clamp(120px,18vw,200px)] font-semibold text-fg/85 tracking-[-0.04em] leading-none">
            {initial}
          </span>
          {/* Soft accent halo behind the initial */}
          <span aria-hidden className="absolute inset-0 rounded-full bg-accent-glow opacity-[0.06] blur-2xl" />
        </motion.div>

        {/* Bio block */}
        <div className="flex flex-col gap-6 max-md:text-center max-md:items-center">
          {eyebrow && (
            <motion.div variants={fadeUp}>
              <Eyebrow>{eyebrow}</Eyebrow>
            </motion.div>
          )}
          {headline && (
            <motion.h2 variants={fadeUp} className="text-display-2 font-display text-fg text-balance max-md:text-center">
              {headline}
            </motion.h2>
          )}
          {body && (
            <motion.p variants={fadeUp} className="text-subhead text-fg-soft max-w-[44ch] text-pretty max-md:text-center">
              {body}
            </motion.p>
          )}
          {citation && (
            <motion.cite variants={fadeUp} className="not-italic text-caption font-mono text-fg-mute mt-4 before:content-['—_'] before:text-fg-faint">
              {citation}
            </motion.cite>
          )}
        </div>
      </motion.div>
    </Stage>
  );
}
